import type { GameAction, PlayerObservationV1, VisibleCardViewV1 } from "@fate-kingdom/engine";

import { alphaPlacementActionsByLane } from "../drag/alpha-drag-types";

export interface AlphaCardAvailability {
  readonly playable: boolean;
  readonly summary: string;
  readonly reasonCode:
    | "wrong-phase"
    | "land-action-used"
    | "deployment-used"
    | "land-required"
    | "commander-required"
    | "capacity"
    | "resources"
    | "no-legal-target"
    | "not-human-turn"
    | "match-finished";
}

function availability(
  playable: boolean,
  reasonCode: AlphaCardAvailability["reasonCode"],
  summary: string,
): AlphaCardAvailability {
  return { playable, reasonCode, summary };
}

export function getAlphaCardAvailability({
  card,
  observation,
  legalActions,
  isHumanTurn,
}: {
  readonly card: VisibleCardViewV1;
  readonly observation: PlayerObservationV1;
  readonly legalActions: readonly GameAction[];
  readonly isHumanTurn: boolean;
}): AlphaCardAvailability {
  if (observation.status.type === "finished") {
    return availability(false, "match-finished", "The match is complete.");
  }
  if (!isHumanTurn) {
    return availability(false, "not-human-turn", "Available when your turn returns.");
  }
  if (
    legalActions.some(
      (action) => action.type === "play-event" && action.cardInstanceId === card.cardInstanceId,
    )
  ) {
    return availability(true, "no-legal-target", "Playable now during your action window.");
  }
  if (alphaPlacementActionsByLane(card.cardInstanceId, legalActions).size > 0) {
    return availability(true, "no-legal-target", "Drag to a glowing lane, or tap then choose one.");
  }
  if (card.type === "land") {
    if (observation.phase !== "land" && observation.phase !== "action-window") {
      return availability(false, "wrong-phase", "Available during your action window.");
    }
    if (observation.own.actionsUsed.landActions > 0) {
      return availability(false, "land-action-used", "Your Land Action is already used this turn.");
    }
  }
  if (card.type === "commander") {
    if (observation.phase !== "command" && observation.phase !== "action-window") {
      return availability(false, "wrong-phase", "Available during your action window.");
    }
    if (observation.own.actionsUsed.deploymentActions > 0) {
      return availability(
        false,
        "deployment-used",
        "Your one Deployment Action is already used. Try next turn.",
      );
    }
    if (Object.values(observation.lanes).every((lane) => lane.own.land === null)) {
      return availability(false, "land-required", "Establish a Land before deploying a Commander.");
    }
  }
  if (card.type === "army") {
    if (observation.phase !== "command" && observation.phase !== "action-window") {
      return availability(false, "wrong-phase", "Available during your action window.");
    }
    if (observation.own.actionsUsed.deploymentActions > 0) {
      return availability(
        false,
        "deployment-used",
        "Your one Deployment Action is already used. Try this Army next turn.",
      );
    }
    const ownLanes = Object.values(observation.lanes).map((lane) => lane.own);
    if (ownLanes.every((lane) => lane.land === null)) {
      return availability(false, "land-required", "Establish a Land before stationing an Army.");
    }
  }
  return availability(
    false,
    "no-legal-target",
    "No legal target right now. Inspect the card for its timing.",
  );
}
